import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createKitchen } from "../../../redux/actions/kitchenActions";
import { useLanguage } from "../../../context/LanguageContext";

const SectionKChallenges = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  const [form, setForm] = useState({
    hasChallenges: "",
    k1: "",
    hasSuggestions: "",
    k2: "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.hasChallenges) newErrors.hasChallenges = t.modules.kitchen.sections.K.errors?.selectYesNo || "Please select Yes or No";
    if (!form.hasSuggestions) newErrors.hasSuggestions = t.modules.kitchen.sections.K.errors?.selectYesNo || "Please select Yes or No";

    if (form.hasChallenges === t.global.yes && !form.k1.trim()) {
      newErrors.k1 = t.modules.kitchen.sections.K.errors?.challengesRequired || "Write down the challenges faced";
    }
    if (form.hasSuggestions === t.global.yes && !form.k2.trim()) {
      newErrors.k2 = t.modules.kitchen.sections.K.errors?.suggestionsRequired || "Write down the suggestions";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      // Update localStorage first
      const surveyData = JSON.parse(localStorage.getItem("surveyData") || "{}");
      surveyData.sectionK = form;
      localStorage.setItem("surveyData", JSON.stringify(surveyData));

      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.k = true; // Mark Section K as done
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      // Check if all sections (a to k) are completed
      const allSections = [
        "a",
        "b",
        "c",
        "d",
        "e",
        "f",
        "g",
        "h",
        "i",
        "j",
        "k",
      ];
      const isSurveyComplete = allSections.every(
        (sec) => completedSections[sec],
      );

      const payload = {
        district_id: state?.districtId || 1,
        municipality_id: state?.municipalityId || 1,
        ward_id: state?.wardId || 5,
        area_id: state?.areaId || 7,
        canteen_type_id: state?.canteenTypeId || 2,
        latitude: state?.latitude || "22.476200",
        longitude: state?.longitude || "88.376984",
        challenges: form.hasChallenges === t.global.yes ? form.k1 : "No challenges",
        suggestions: form.hasSuggestions === t.global.yes ? form.k2 : "No suggestions",
        is_total_survey_complete: isSurveyComplete ? 1 : 0,
        check_survey_completed: isSurveyComplete ? 1 : 0,
        current_section: isSurveyComplete ? "final" : "draftK",
        reporting_date: new Date().toISOString().split("T")[0],
      };

      console.log("SENDING TO BACKEND:", payload);
      
      // Dispatch API call
      await dispatch(createKitchen(payload));

      // alert(t.global.success_message);

      navigate("/survey/sections", { state });
    } catch (err) {
      console.error("Error saving Section K:", err);
      alert(t.global.alert_server_error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kitchen.sections.K.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kitchen.sections.K.subtitle}
              </p>
            </div>
            <div className="text-sm bg-red-100 text-red-800 px-3 py-1 rounded-full">
              {state?.canteenId
                ? `${t.global.canteen_id_label} ${state.canteenId}`
                : state?.kioskId
                  ? `${t.global.canteen_id_label} ${state.kioskId}`
                  : t.global.na}
            </div>
          </div>

          <div className="space-y-6">
            {/* K1 - Challenges */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center mb-3">
                <div className="bg-gray-100 text-gray-800 font-bold rounded-full h-8 w-8 flex items-center justify-center mr-3">
                  1
                </div>
                <label className="block font-medium">
                  {t.modules.kitchen.sections.K.questions.challenges} <span className="text-red-500">*</span>
                </label>
              </div>

              {/* YES/NO Radio Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-3 border rounded cursor-pointer ${
                      form.hasChallenges === option
                        ? "bg-blue-50 border-blue-300"
                        : ""
                    } ${errors.hasChallenges ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="hasChallenges"
                      value={option}
                      checked={form.hasChallenges === option}
                      onChange={handleChange}
                      className="mr-3"
                    />
                    {option}
                  </label>
                ))}
              </div>
              {errors.hasChallenges && (
                <p className="text-red-500 text-sm mb-2">
                  {errors.hasChallenges}
                </p>
              )}

              {/* Conditional Textarea */}
              {form.hasChallenges === t.global.yes && (
                <textarea
                  name="k1"
                  placeholder={t.modules.kitchen.sections.K.placeholders?.challenges || "Describe any challenges faced..."}
                  className={`w-full border p-4 rounded-lg ${errors.k1 ? "border-red-500" : ""}`}
                  rows="4"
                  value={form.k1}
                  onChange={handleChange}
                />
              )}
              {errors.k1 && (
                <p className="text-red-500 text-sm mt-2">{errors.k1}</p>
              )}
            </div>

            {/* K2 - Suggestions */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center mb-3">
                <div className="bg-gray-100 text-gray-800 font-bold rounded-full h-8 w-8 flex items-center justify-center mr-3">
                  2
                </div>
                <label className="block font-medium">
                  {t.modules.kitchen.sections.K.questions.suggestions} <span className="text-red-500">*</span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-3 border rounded cursor-pointer ${
                      form.hasSuggestions === option
                        ? "bg-blue-50 border-blue-300"
                        : ""
                    } ${errors.hasSuggestions ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="hasSuggestions"
                      value={option}
                      checked={form.hasSuggestions === option}
                      onChange={handleChange}
                      className="mr-3"
                    />
                    {option}
                  </label>
                ))}
              </div>
              {errors.hasSuggestions && (
                <p className="text-red-500 text-sm mb-2">
                  {errors.hasSuggestions}
                </p>
              )}

              {form.hasSuggestions === t.global.yes && (
                <textarea
                  name="k2"
                  placeholder={t.modules.kitchen.sections.K.placeholders?.suggestions || "Provide suggestions for improvement..."}
                  className={`w-full border p-4 rounded-lg ${errors.k2 ? "border-red-500" : ""}`}
                  rows="4"
                  value={form.k2}
                  onChange={handleChange}
                />
              )}
              {errors.k2 && (
                <p className="text-red-500 text-sm mt-2">{errors.k2}</p>
              )}
            </div>

            {/* Required fields note */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <span className="text-red-500">*</span> {t.modules.kitchen.sections.K.requiredNote || "indicates required fields. Both challenges and suggestions must be provided."}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                {t.global.back_to_sections}
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                {t.global.submit_section_k || "Save Section K"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionKChallenges;